import type { HrtOption } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/ProductContext";
import { useBoundStore } from "@frontend/lib/stores/globalStore";

export const areHrtSelfPayOnly = (options: HrtOption[]) => {
  return !options.every((option) => {
    const primaryHrtInsured = option.hrt.hrtPaymentType.some(
      (paymentType) => paymentType.paymentType === "INSURED",
    );
    if (option.defaultHrt) {
      // Assuming defaultHrt has the same structure
      return (
        primaryHrtInsured &&
        option.defaultHrt.hrtPaymentType.some(
          (paymentType) => paymentType.paymentType === "INSURED",
        )
      );
    }
    return primaryHrtInsured;
  });
};

interface PriceData {
  price: number;
  pricedTreatmentId: number;
  pricingExplanation?: string;
}

interface HrtPricing {
  priceData: {
    CASH: PriceData | undefined;
    INSURED: PriceData | undefined;
  };
  mode: "CASH" | "INSURED";
}

export type KioskPricingMap = Record<string, HrtPricing>;

interface KioskPriceMapProps {
  pricingMap?: KioskPricingMap;
  onPriceModeChange?: (pricingMap: KioskPricingMap) => void;
}

export const usePriceMap = (
  options: HrtOption[],
  kioskProps?: KioskPriceMapProps,
) => {
  // Use kiosk pricing map if provided, otherwise use global store
  const globalPricingMap = useBoundStore.use.pricingMapEntries();
  const globalSetPricingMap = useBoundStore.use.setPricingMap();
  const globalStorefrontPaymentMode = useBoundStore.use.storefrontPaymentMode();

  const pricingMapEntries = kioskProps?.pricingMap || globalPricingMap;
  const setPricingMap = kioskProps?.onPriceModeChange || globalSetPricingMap;
  const storefrontPaymentMode = kioskProps?.pricingMap
    ? "INSURED"
    : globalStorefrontPaymentMode;

  // Get the default option first
  const defaultOption = options.find(
    (opt) => opt.product_component_type === "DEFAULT",
  );

  // Enhance options with defaultHrt where needed
  const enhancedOptions = options.map((option) => {
    if (option.product_component_type === "INITIAL_REQUIRED" && defaultOption) {
      return {
        ...option,
        defaultHrt: defaultOption.hrt,
      };
    }
    return option;
  });

  const handlePriceModeChange = (checked: boolean) => {
    const newPricingMap = { ...pricingMapEntries };
    for (const option of options) {
      // Update pricing mode for both DEFAULT and INITIAL_REQUIRED options
      if (
        (option.product_component_type === "DEFAULT" ||
          option.product_component_type === "INITIAL_REQUIRED") &&
        newPricingMap[option.hrt.hrtId]
      ) {
        newPricingMap[option.hrt.hrtId] = {
          ...newPricingMap[option.hrt.hrtId],
          mode: checked ? "INSURED" : "CASH",
        };

        // Also update the defaultHrt pricing mode if it exists
        if (
          option.product_component_type === "INITIAL_REQUIRED" &&
          option.defaultHrt &&
          newPricingMap[option.defaultHrt.hrtId]
        ) {
          newPricingMap[option.defaultHrt.hrtId] = {
            ...newPricingMap[option.defaultHrt.hrtId],
            mode: checked ? "INSURED" : "CASH",
          };
        }
      }
    }

    setPricingMap(newPricingMap);
  };

  // Determine isInsuredMode by checking pricing map entries first,
  // but fall back to storefrontPaymentMode if entries don't have a mode set
  const defaultOptions = options.filter(
    (option) => option.product_component_type === "DEFAULT",
  );

  const hasAllModesSet = defaultOptions.every(
    (option) => pricingMapEntries[option.hrt.hrtId]?.mode !== undefined,
  );

  const isInsuredMode = hasAllModesSet
    ? defaultOptions.every(
        (option) => pricingMapEntries[option.hrt.hrtId]?.mode === "INSURED",
      )
    : storefrontPaymentMode === "INSURED";

  // Ensure we have at least one option
  const finalIsInsuredMode = isInsuredMode && options.length > 0;

  const isToggleDisabled = options.some((option) => {
    const primaryHrtDisabled =
      pricingMapEntries[option.hrt.hrtId]?.priceData.INSURED === undefined;
    if (option.defaultHrt) {
      return (
        primaryHrtDisabled ||
        pricingMapEntries[option.defaultHrt.hrtId]?.priceData.INSURED ===
          undefined
      );
    }
    return primaryHrtDisabled;
  });

  const isSelfPayOnly = areHrtSelfPayOnly(options);

  const [lowestPrice, lowestComparisonPrice] = enhancedOptions.reduce(
    ([minPrice, minComparisonPrice]: [number, number], option) => {
      if (option.hrt.isConsumerFacing === false) {
        return [minPrice, minComparisonPrice];
      }

      const calculateOptionPrice = (
        hrtId: number,
        mode: "INSURED" | "CASH",
      ) => {
        const priceEntry = pricingMapEntries[hrtId];
        const fallBackHrtCashPrice =
          mode === "CASH" && option.hrt.cash_prices?.cashPrice
            ? option.hrt.cash_prices?.cashPrice / 100
            : Number.POSITIVE_INFINITY;
        const optionsPrice =
          priceEntry?.priceData[mode]?.price ||
          priceEntry?.priceData[mode]?.price === 0
            ? priceEntry?.priceData[mode]?.price
            : fallBackHrtCashPrice;

        return optionsPrice;
      };

      // Calculate prices based on option type
      let optionInsuredPrice = calculateOptionPrice(
        option.hrt.hrtId,
        "INSURED",
      );
      let optionCashPrice = calculateOptionPrice(option.hrt.hrtId, "CASH");

      // For INITIAL_REQUIRED, add the DEFAULT price
      if (
        option.product_component_type === "INITIAL_REQUIRED" &&
        option.defaultHrt
      ) {
        optionInsuredPrice += calculateOptionPrice(
          option.defaultHrt.hrtId,
          "INSURED",
        );
        optionCashPrice += calculateOptionPrice(
          option.defaultHrt.hrtId,
          "CASH",
        );
      }

      const priceEntry = pricingMapEntries[option.hrt.hrtId];
      // Use priceEntry mode if available, otherwise fall back to storefrontPaymentMode
      const effectiveMode = priceEntry?.mode ?? storefrontPaymentMode;
      const [primaryPrice, secondaryPrice] =
        effectiveMode === "INSURED"
          ? [optionInsuredPrice, optionCashPrice]
          : [optionCashPrice, optionInsuredPrice];

      return [
        Math.min(minPrice, primaryPrice),
        Math.min(minComparisonPrice, secondaryPrice),
      ];
    },
    [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
  );

  return {
    storefrontPaymentMode,
    isInsuredMode: finalIsInsuredMode,
    handlePriceModeChange,
    isToggleDisabled,
    lowestPrice,
    lowestComparisonPrice,
    isSelfPayOnly,
  };
};
